import React, { useEffect } from "react";
import { Redirect, withRouter } from "react-router-dom";
import { connect } from "react-redux";
import { parse } from "query-string";
import I18n from "../../I18n";
import { mapProperties, hydrateRoute } from "colbi_web_ui/lib/utils";
import {
    downloadAction,
    downloadCsvStreamAction,
    logout,
    pushModal,
    pushDialog,
    popModal,
    toggleMenu,
    setLocale,
    removeModal,
    saveWorkerAssociationPackageAction,
} from "../../store/actions";

import { DataGrid, LoadingIndicator, rows } from "colbi_web_ui/lib/components";
import { SovosBox, SovosPageLayout, SovosPageContentGeneric } from "mosaic-react";
import ModalController from "../App/ModalController/ModalController";

import styles from "./Settings.module.sass";

import { UserRoles } from "../../enums/userRoles";
import {
    decodeUserSession,
    redirectNotValid,
    BuildSovosNavProps,
} from "../../utils/utils";

import { checkAccessByRole } from "../../utils/roleValidatorUtil";
import GlobalLayout from "../../components/GlobalLayout/GlobalLayout";
import { useAuth } from "react-oidc-context";
import {
    buildSettingsStaticLinks,
    generateSettingsMenuItems,
} from "../../components/GlobalLayout/menuBuilderUtils";
import { PackageAssociationProvider } from "../../components/PackageAssociation/PackageAssociationContext";
import PackageAssociationSaveButton from "../../components/PackageAssociation/PackageAssociationSaveButton";
import PackageAssociationTabs from "../../components/PackageAssociation/PackageAssociationTabs";
import PackageAssociationToolbar from "../../components/PackageAssociation/PackageAssociationToolbar";
import { packagesAssociationQueries } from "./packagesAssociationQueries";
import {
    getSettingsContentSx,
    getSettingsGridModifiersSx,
    getSettingsPageSx,
    packageAssociationListFitsViewport,
    resolveSettingsListTotal,
} from "./settingsGridLayout";

const DEFAULT_ENTITY = "package_rules";
const BASE_PATH = "/:locale(pt|en|lt|es|ro|fr)?/settings/:section/packages-association/:id";

const PackagesAssociation = (props) => {
    const { user, fetch, match, setLocale, location } = props;

    const locale = match.params.locale || "en";
    const i18n = I18n.use(locale);
    const auth = useAuth();

    useEffect(() => {
        setLocale(locale);
    }, [locale, setLocale]);

    const userInformation = decodeUserSession(user);

    const menuItems = generateSettingsMenuItems(userInformation?.role, props.history, locale, i18n) || [];
    const navigation = menuItems.map((elem) => ({
        ...elem,
        name: I18n.translate`${elem.name}`,
    }));
    const setupLinks = buildSettingsStaticLinks(navigation, props.history, match.params, i18n);

    const redirect = redirectNotValid(userInformation, locale, true, auth);

    if (redirect) {
        return redirect;
    }

    const isAdminRoles = !checkAccessByRole(userInformation, [
        UserRoles.OWNER,
        UserRoles.READER,
        UserRoles.WRITER,
    ]);

    if (!(userInformation && userInformation.accessSettings) || !isAdminRoles) {
        return <Redirect to={`/${locale}/unauthorized`} />;
    }

    const queryParams = parse(location.search) || {};
    const routeParams = match.params || {};
    const params = {
        ...queryParams,
        ...routeParams,
    };

    if (!params.entity) {
        const hydrated = hydrateRoute(`${BASE_PATH}/:entity`, {
            ...params,
            entity: DEFAULT_ENTITY,
        });
        return <Redirect to={`${hydrated}${location.search}`} replace />;
    }

    const queries = packagesAssociationQueries(params);
    const downloadableQueries = queries.filter(({ downloadable }) => downloadable);

    const data = mapProperties(
        {
            id: "results",
            card: rows.CellCard,
            items: "query(0).list",
            header: "query(0).header",
            footer: "query(0).footer",
            metadata: "query(0).metadata",
            status: "status(0)",
            filter: "param(filter)",
            sort: "param(sort)",
            availableFilters: "query(0).filters",
            wrapper: "wrapper",
            total: "query(0).metadata.total",
            pageSize: 20000,
            enablePagination: false,
            toolbar: PackageAssociationToolbar,
            toolbarProps: {
                data: "query(0).list",
            },
        },
        (fetch || {}).results,
        (fetch || {}).status,
        queries,
        props,
        params,
        i18n,
        locale,
    );

    const rowCount = data?.items?.length ?? 0;
    const isLoading = data?.status?.loading ?? false;
    const useAutoHeight = !isLoading && packageAssociationListFitsViewport(rowCount);

    const gridMetadata = {
        ...(data?.metadata || {}),
        total: resolveSettingsListTotal({
            items: data?.items,
            metadata: data?.metadata,
            status: data?.status,
        }),
    };

    const gridKey = `${params.entity}-${params.id}-${params.filter || ""}-${params.sort || ""}`;

    const navProps = BuildSovosNavProps(
        undefined,
        [],
        { name: "" },
        {},
        userInformation,
        setupLinks || [],
    );

    const globalLayoutProps = {
        ...props,
        navProps,
        downloadableQueries,
        params,
        staticLinks: setupLinks,
        styles,
    };

    return (
        <GlobalLayout {...globalLayoutProps}>
            <PackageAssociationProvider items={data?.items} entity={params.entity}>
                <SovosPageLayout
                    pageTitle={i18n`Packages Association`}
                    titleBarActions={[
                        <PackageAssociationSaveButton key="packageAssociationSave" />,
                    ]}
                >
                    <SovosBox sx={getSettingsPageSx(useAutoHeight)}>
                        <SovosPageContentGeneric sx={getSettingsContentSx(useAutoHeight)}>
                            {fetch.activity && (
                                <LoadingIndicator className={`${styles["loading-indicator"]}`} />
                            )}
                            <PackageAssociationTabs />
                            <DataGrid
                                key={gridKey}
                                {...(data || {})}
                                metadata={gridMetadata}
                                autoHeight={useAutoHeight}
                                enablePagination={false}
                                modifierssx={getSettingsGridModifiersSx(useAutoHeight)}
                            />
                            <ModalController />
                        </SovosPageContentGeneric>
                    </SovosBox>
                </SovosPageLayout>
            </PackageAssociationProvider>
        </GlobalLayout>
    );
};

PackagesAssociation.queries = packagesAssociationQueries;

export default connect(
    ({ user, fetch, menu, uploads }) => ({
        user,
        fetch,
        menu,
        uploads,
    }),
    {
        toggleMenu,
        logout,
        setLocale,
        pushModal,
        popModal,
        removeModal,
        pushDialog,
        saveWorkerAssociationPackage: saveWorkerAssociationPackageAction,
        download: downloadAction,
        downloadCsvStreamAction,
    },
)(withRouter(PackagesAssociation));
